import { pb } from "@/lib/pocketbase";

export interface AstroConnectionTestRequest {
  brandId: string;
  repositoryUrl: string;
  branch: string;
  accessToken: string;
  contentPath: string;
}

export interface AstroUpdateSettingsRequest {
  brandId: string;
  defaultAuthor?: string;
  defaultTags?: string[];
  commitMessageTemplate?: string;
  autoPublish?: boolean;
  createPullRequest?: boolean;
}

export interface AstroTestResponse {
  status: string;
  integration: {
    id: string;
    type: string;
    status: string;
    brand: string;
    category: string;
  };
}

export interface AstroSettingsResponse {
  message: string;
  settings: {
    defaultAuthor?: string;
    defaultTags?: string[];
    commitMessageTemplate?: string;
    autoPublish?: boolean;
    createPullRequest?: boolean;
  };
}

export interface AstroPublishRequest {
  postId: string;
  brandId: string;
}

export interface AstroPublishResponse {
  status: string;
  file_path: string;
  link: string;
  branch: string;
}

class AstroService {
  private baseURL = pb.baseURL;

  async testConnection(
    request: AstroConnectionTestRequest,
  ): Promise<AstroTestResponse> {
    const response = await fetch(
      `${this.baseURL}/api/v1/astro/test-connection`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: pb.authStore.token,
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to test Astro connection");
    }

    return response.json();
  }

  async connect(
    request: AstroConnectionTestRequest,
  ): Promise<AstroTestResponse> {
    // For Astro, test-connection also saves the integration, so we use the same endpoint
    return this.testConnection(request);
  }

  async updateSettings(
    request: AstroUpdateSettingsRequest,
  ): Promise<AstroSettingsResponse> {
    const response = await fetch(
      `${this.baseURL}/api/v1/astro/update-settings`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: pb.authStore.token,
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to update Astro settings");
    }

    return response.json();
  }

  async publish(data: AstroPublishRequest): Promise<AstroPublishResponse> {
    const response = await fetch(`${this.baseURL}/api/v1/astro/publish`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: pb.authStore.token,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to publish to Astro");
    }

    return response.json();
  }
}

export const astroService = new AstroService();
